/**
 * Regras puras da cotação conjunta (2026-10-02): um fornecedor, vários itens
 * da mesma requisição, um frete e um prazo geral.
 *
 * O backend é quem grava e rateia (`compras/services_fila.ratear_frete`);
 * aqui o rateio só faz a PRÉVIA, com a mesma regra, para o comprador ver o
 * frete e o total de cada item antes de salvar.
 */
import { parseDinheiro } from "../../../utils/dinheiro";

const SITUACOES_ENCERRADAS = new Set(["COMPRADO", "CANCELADO"]);

/** Item que ainda recebe cotação. */
export const itemCotavel = (it) => !SITUACOES_ENCERRADAS.has(it?.situacao);

export const qtdDoItem = (it) => Number(it?.qtd_aprovada || it?.qtd_solicitada || 0);

// Mesmo arredondamento do Decimal.quantize do backend (meio para o par), em
// centavos inteiros para não acumular erro de ponto flutuante.
function arredondarMeioParaPar(x) {
  const piso = Math.floor(x);
  const resto = x - piso;
  if (Math.abs(resto - 0.5) < 1e-9) return piso % 2 === 0 ? piso : piso + 1;
  return Math.round(x);
}

/**
 * Divide o frete pelo valor de cada item. Base nula/zero fica fora (item "não
 * tem" ou sem preço); os centavos que sobram vão para o último que entra.
 * Devolve `null` quando há frete e nenhum item com valor (o backend recusa).
 */
export function ratearFrete(freteTotal, bases) {
  const totalCentavos = Math.round(Number(freteTotal || 0) * 100);
  const pesos = bases.map((b) => (b && b > 0 ? b : 0));
  const soma = pesos.reduce((a, b) => a + b, 0);
  if (!soma) return totalCentavos > 0 ? null : bases.map(() => 0);
  const partes = pesos.map((p) => arredondarMeioParaPar((totalCentavos * p) / soma));
  const ultimo = pesos.reduce((u, p, i) => (p > 0 ? i : u), -1);
  partes[ultimo] += totalCentavos - partes.reduce((a, b) => a + b, 0);
  return partes.map((c) => c / 100);
}

const textoDoValor = (v) => (v === null || v === undefined || v === ""
  ? ""
  : Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 }));

/** Uma linha por item; se o fornecedor já cotou, vem preenchida. */
export function linhasIniciais(itens, fornecedorId) {
  return itens.map((it) => {
    const atual = fornecedorId ? (it.cotacoes || []).find((c) => c.fornecedor === fornecedorId) : null;
    return {
      item: it,
      valor: atual && !atual.nao_tem ? textoDoValor(atual.valor_unitario) : "",
      naoTem: Boolean(atual?.nao_tem),
      prazo: atual?.prazo_dias !== null && atual?.prazo_dias !== undefined ? String(atual.prazo_dias) : "",
      vencedora: Boolean(atual?.vencedora),
      jaCotado: Boolean(atual),
    };
  });
}

const inteiroOuNulo = (t) => {
  const s = String(t ?? "").trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isInteger(n) && n >= 0 ? n : NaN;
};

/** Frete, total por item e total do fornecedor, como vão ficar depois de salvar. */
export function previaDaCotacao(linhas, freteTexto) {
  const bases = linhas.map((l) => {
    const v = l.naoTem ? null : parseDinheiro(l.valor);
    return v === null ? null : v * qtdDoItem(l.item);
  });
  const frete = parseDinheiro(freteTexto);
  const fretes = frete === null ? linhas.map(() => null) : ratearFrete(frete, bases);
  const porLinha = linhas.map((l, i) => ({
    base: bases[i],
    frete: fretes ? fretes[i] : null,
    total: bases[i] === null ? null : bases[i] + (fretes?.[i] || 0),
  }));
  return {
    linhas: porLinha,
    freteSemItem: fretes === null,
    total: porLinha.reduce((s, l) => s + (l.total || 0), 0),
  };
}

/**
 * Monta o corpo do POST ou devolve `{ erro }` com o que a pessoa precisa
 * corrigir. Frete em branco = não mexe no frete já gravado.
 */
export function payloadDaCotacao({ fornecedor, prazoGeral, frete, observacao, linhas }) {
  if (!fornecedor) return { erro: "Escolha o fornecedor." };
  if (!linhas.length) return { erro: "Selecione ao menos um item." };
  const prazo = inteiroOuNulo(prazoGeral);
  if (Number.isNaN(prazo)) return { erro: "Prazo geral: informe dias inteiros." };
  const freteNum = parseDinheiro(frete);
  if (String(frete || "").trim() && freteNum === null) return { erro: "Frete inválido." };
  if (freteNum !== null && freteNum < 0) return { erro: "Frete não pode ser negativo." };
  const saida = [];
  for (const l of linhas) {
    const nome = l.item.produto_nome || l.item.descricao || "Item";
    const valor = l.naoTem ? null : parseDinheiro(l.valor);
    if (!l.naoTem && String(l.valor || "").trim() && valor === null) return { erro: `${nome}: valor inválido.` };
    if (valor !== null && valor < 0) return { erro: `${nome}: valor não pode ser negativo.` };
    if (l.naoTem && l.vencedora) {
      return { erro: `${nome}: este fornecedor é o vencedor; escolha outro antes de marcar "não tem".` };
    }
    const prazoLinha = inteiroOuNulo(l.prazo);
    if (Number.isNaN(prazoLinha)) return { erro: `${nome}: prazo em dias inteiros.` };
    saida.push({
      item: l.item.id,
      valor_unitario: valor === null ? null : String(valor),
      nao_tem: Boolean(l.naoTem),
      prazo_dias: prazoLinha,
    });
  }
  const { freteSemItem } = previaDaCotacao(linhas, frete);
  if (freteSemItem) return { erro: "O frete precisa de ao menos um item com valor." };
  return {
    payload: {
      fornecedor,
      prazo_dias: prazo,
      frete_total: freteNum === null ? null : String(freteNum),
      observacao: observacao || "",
      linhas: saida,
    },
  };
}

/**
 * Fornecedores que podem vencer os itens selecionados: quem tem valor em ao
 * menos um deles, com quantos ganharia. Ordem: quem cobre mais, depois nome.
 */
export function candidatosAVencedor(itens) {
  const por = new Map();
  itens.filter(itemCotavel).forEach((it) => {
    (it.cotacoes || []).forEach((c) => {
      if (c.nao_tem || c.valor_unitario === null || c.valor_unitario === undefined) return;
      const atual = por.get(c.fornecedor) || { id: c.fornecedor, nome: c.fornecedor_nome, itens: 0, total: 0 };
      atual.itens += 1;
      atual.total += Number(c.total_com_frete || 0);
      por.set(c.fornecedor, atual);
    });
  });
  return [...por.values()].sort((a, b) => b.itens - a.itens || a.nome.localeCompare(b.nome));
}
